/**
 * WatchdogConfigModal — DEVICE-DETAIL-MANAGE-1D.
 *
 * Configura o Watchdog de conectividade do device a partir da página Detalhes,
 * usando o endpoint JÁ existente e guardado:
 *   POST /api/admin/devices/{id}/config/watchdog  (configWatchdog em api.js)
 *
 * Backend = fonte da verdade dos guards: exige CAP_WATCHDOG_WRITE (botão gated por
 * CAP.WATCHDOG_WRITE) + confirmação forte SEMPRE (S3.10C1 — o watchdog pode
 * reiniciar o relé/ESP automaticamente). Em device protegido, a denylist segue
 * valendo no backend. NÃO cria comando fora deste endpoint; NÃO toca firmware/HMAC.
 *
 * Estado atual pré-preenchido de data.watchdog_runtime (reportado pelo firmware).
 * Contrato (ConfigWatchdogIn): enabled, host1(obrig. se enabled), host2, mode
 * (ALL_FAIL|ANY_FAIL), action (relay_reboot|esp_reboot|alert_only), interval_s
 * 10..3600, timeout_s 1..60, reboot_after_fails 1..100, cooldown_s 10..86400,
 * reboot_duration_s 1..300.
 */
import React, { useMemo, useState } from "react";

import { configWatchdog } from "../lib/api.js";
import { useCriticalConfirm } from "./ConfirmCriticalAction.jsx";

const INPUT =
  "w-full bg-noc-bg border border-noc-border rounded-md px-2 py-1.5 " +
  "text-[13px] text-noc-text focus:border-noc-accent outline-none " +
  "disabled:opacity-40";

const LIMITS = {
  interval_s: [10, 3600],
  timeout_s: [1, 60],
  reboot_after_fails: [1, 100],
  cooldown_s: [10, 86400],
  reboot_duration_s: [1, 300],
};

function friendlyErr(e) {
  const s = e?.status;
  if (s === 401) return "Sessão expirada — faça login novamente.";
  if (s === 403) return "Sem permissão (watchdog:write) ou confirmação recusada.";
  if (s === 404) return "Device não encontrado.";
  if (s === 422) return `Dado inválido: ${e?.message || "verifique os campos."}`;
  return e?.message || "Falha ao configurar o watchdog.";
}

export default function WatchdogConfigModal({ deviceId, device, watchdog, onClose, onSaved }) {
  const confirmCritical = useCriticalConfirm();
  const protectedDev = !!device?.ota_protected;
  const wr = watchdog || {};

  // timeout_s não é recuperável do runtime sem perda (janela = timeout × failCnt);
  // derivamos um valor sensato (janela / max_fails) para o operador revisar.
  const derivedTimeout = useMemo(() => {
    if (wr.timeout_window_s && wr.max_fails) {
      const t = Math.round(wr.timeout_window_s / wr.max_fails);
      return String(Math.min(60, Math.max(1, t)));
    }
    return "";
  }, [wr.timeout_window_s, wr.max_fails]);

  const [enabled, setEnabled] = useState(!!wr.enabled);
  const [host1, setHost1] = useState(wr.host1 || "");
  const [host2, setHost2] = useState(wr.host2 || "");
  const [mode, setMode] = useState(wr.mode || "ANY_FAIL");
  const [action, setAction] = useState(wr.action || "relay_reboot");
  const [intervalS, setIntervalS] = useState(wr.interval_s != null ? String(wr.interval_s) : "");
  const [timeoutS, setTimeoutS] = useState(derivedTimeout);
  const [rebootAfterFails, setRebootAfterFails] = useState(wr.max_fails != null ? String(wr.max_fails) : "");
  const [cooldownS, setCooldownS] = useState(wr.cooldown_s != null ? String(wr.cooldown_s) : "");
  const [rebootDurationS, setRebootDurationS] = useState(
    wr.reboot_duration_s != null ? String(wr.reboot_duration_s) : "");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  function numErr(key, val) {
    if (String(val).trim() === "") return null; // opcional vazio → omitido
    const n = Number(val);
    const [lo, hi] = LIMITS[key];
    if (!Number.isInteger(n) || n < lo || n > hi) return `${key} deve ser inteiro entre ${lo} e ${hi}.`;
    return null;
  }

  function validate() {
    if (enabled && !host1.trim()) return "Informe o Host 1 (obrigatório quando habilitado).";
    for (const key of Object.keys(LIMITS)) {
      const val = { interval_s: intervalS, timeout_s: timeoutS, reboot_after_fails: rebootAfterFails,
        cooldown_s: cooldownS, reboot_duration_s: rebootDurationS }[key];
      const msg = numErr(key, val);
      if (msg) return msg;
    }
    return null;
  }

  function buildBody() {
    const body = { enabled };
    if (!enabled) return body; // desabilitar só precisa de enabled=false
    body.host1 = host1.trim();
    if (host2.trim()) body.host2 = host2.trim();
    if (mode) body.mode = mode;
    if (action) body.action = action;
    const nums = { interval_s: intervalS, timeout_s: timeoutS, reboot_after_fails: rebootAfterFails,
      cooldown_s: cooldownS, reboot_duration_s: rebootDurationS };
    for (const [k, v] of Object.entries(nums)) {
      if (String(v).trim() !== "") body[k] = Number(v);
    }
    return body;
  }

  async function handleSave() {
    const v = validate();
    if (v) { setError(v); return; }
    setError(null);
    // S3.10C1 — watchdog SEMPRE exige confirmação forte (auto-reboot do relé/ESP).
    const secret = await confirmCritical({
      actionLabel: enabled ? "Configurar Watchdog" : "Desabilitar Watchdog",
      target: { kind: "single", deviceId },
      impact: protectedDev
        ? "Device PROTEGIDO. O watchdog pode reiniciar o relé/ESP automaticamente em campo."
        : "O watchdog pode reiniciar o relé/ESP automaticamente em campo.",
    });
    if (secret == null) return; // cancelado
    setSaving(true);
    try {
      await configWatchdog(deviceId, buildBody(), { confirm: secret });
      onSaved && onSaved();
    } catch (e) {
      setError(friendlyErr(e));
      setSaving(false);
    }
  }

  const numField = (label, key, val, set, ph) => (
    <div className="space-y-1">
      <label className="text-[11px] uppercase tracking-wide text-noc-faint">
        {label} <span className="text-noc-muted/60">({LIMITS[key][0]}–{LIMITS[key][1]})</span>
      </label>
      <input className={INPUT} inputMode="numeric" placeholder={ph} value={val}
        disabled={!enabled} onChange={(e) => set(e.target.value)} />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={saving ? undefined : onClose}>
      <div className="bg-noc-panel border border-noc-border rounded-xl w-full max-w-lg
                      max-h-[90vh] overflow-y-auto p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-[15px] font-semibold text-noc-text">Configurar Watchdog</h2>
          <span className="font-mono text-[11px] text-noc-faint">{deviceId}</span>
        </div>

        <div className="text-[12px] bg-noc-warn/10 border border-noc-warn/30 text-noc-warn
                        rounded-md px-3 py-2">
          ⚠ O watchdog pode <b>reiniciar o relé/ESP automaticamente</b> em campo. Exige
          confirmação forte ao salvar.{protectedDev && " 🔒 Device PROTEGIDO (produção/gold/canário)."}
        </div>

        {error && (
          <div className="text-[12px] bg-noc-err/10 border border-noc-err/30 text-noc-err
                          rounded-md px-3 py-2">{error}</div>
        )}

        {/* habilitado */}
        <label className="flex items-center gap-2 text-[13px] text-noc-text">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          Watchdog habilitado
        </label>

        {/* hosts */}
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <label className="text-[11px] uppercase tracking-wide text-noc-faint">Host 1 (obrigatório)</label>
            <input className={INPUT} placeholder="ex.: 8.8.8.8" value={host1}
              disabled={!enabled} onChange={(e) => setHost1(e.target.value)} />
          </div>
          <div className="space-y-1">
            <label className="text-[11px] uppercase tracking-wide text-noc-faint">Host 2 (opcional)</label>
            <input className={INPUT} placeholder="ex.: 10.10.10.2" value={host2}
              disabled={!enabled} onChange={(e) => setHost2(e.target.value)} />
          </div>
        </div>

        {/* modo / ação */}
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <label className="text-[11px] uppercase tracking-wide text-noc-faint">Modo</label>
            <select className={INPUT} value={mode} disabled={!enabled}
              onChange={(e) => setMode(e.target.value)}>
              <option value="ANY_FAIL">ANY_FAIL (qualquer host falhar)</option>
              <option value="ALL_FAIL">ALL_FAIL (ambos falharem)</option>
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-[11px] uppercase tracking-wide text-noc-faint">Ação</label>
            <select className={INPUT} value={action} disabled={!enabled}
              onChange={(e) => setAction(e.target.value)}>
              <option value="relay_reboot">relay_reboot (reinicia o relé)</option>
              <option value="esp_reboot">esp_reboot (reinicia o ESP)</option>
              <option value="alert_only">alert_only (só alerta)</option>
            </select>
          </div>
        </div>

        {/* timings */}
        <div className="grid grid-cols-2 gap-2">
          {numField("Intervalo (s)", "interval_s", intervalS, setIntervalS, "ex.: 60")}
          {numField("Timeout (s)", "timeout_s", timeoutS, setTimeoutS, "ex.: 10")}
          {numField("Falhas p/ reboot", "reboot_after_fails", rebootAfterFails, setRebootAfterFails, "ex.: 10")}
          {numField("Cooldown (s)", "cooldown_s", cooldownS, setCooldownS, "ex.: 3600")}
          {numField("Duração reboot relé (s)", "reboot_duration_s", rebootDurationS, setRebootDurationS, "ex.: 10")}
        </div>
        {derivedTimeout && (
          <div className="text-[11px] text-noc-faint">
            Timeout pré-preenchido do runtime (janela {wr.timeout_window_s}s ÷ {wr.max_fails} falhas);
            ajuste se necessário.
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-1">
          <button className="btn-ghost" onClick={onClose} disabled={saving}>Cancelar</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? "Aplicando…" : "Salvar configuração"}
          </button>
        </div>
      </div>
    </div>
  );
}
